import { Button, ConfigProvider, Flex, Modal, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import {
  ModalHeader,
  modalBlockLabelStyle,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { clientDeactivationCheckOptions } from "hooks/queries/user";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { getBookingStatusLabel } from "shared/utils";
import { DEFAULT_DATE_FORMAT, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

const rowStyle = {
  width: "100%",
  boxSizing: "border-box" as const,
  textAlign: "left" as const,
  padding: "10px 12px",
  border: `1px solid ${colors.line}`,
  borderRadius: 10,
  background: colors.card,
  cursor: "pointer",
  font: "inherit",
};

/**
 * Nonaktifkan on a client asks the server first what the deactivation would
 * run into. Bookings still in progress (Pending, Confirmed or Waitlist,
 * whatever the class date) block it: the pop-up lists them, each opening its
 * class, and offers only Tutup. Otherwise it is the confirm, naming the live
 * packages the client leaves behind and any shared one that stays manageable
 * because a co-sharer still uses it. The deactivate PUT re-checks, so a
 * booking made in the meantime is still refused.
 *
 * A component, not showConfirmModal: the rows navigate, which needs the
 * router context a static Modal.confirm doesn't have.
 */
export const DeactivateClientModal = ({
  open,
  userId,
  deactivating,
  onConfirm,
  onClose,
}: {
  open: boolean;
  userId: string;
  deactivating: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(clientDeactivationCheckOptions(userId, open));
  const blocking = data?.blocking_bookings ?? [];
  const livePackages = data?.live_packages ?? [];

  const body = () => {
    if (isLoading) {
      return (
        <Flex justify="center" style={{ padding: "28px 0" }}>
          <Spin />
        </Flex>
      );
    }
    if (blocking.length > 0) {
      return (
        <>
          <ModalHeader
            tone="amber"
            title={translate("client_status.deactivate.blocked_title")}
          />
          <Text style={modalSubtitleStyle}>
            {translate("client_status.deactivate.blocked_body")}
          </Text>
          <Flex vertical gap={8} style={{ marginTop: 16 }}>
            {blocking.map((b) => (
              <button
                key={b.id}
                type="button"
                style={rowStyle}
                onClick={() => {
                  onClose();
                  navigate(
                    `${PATHS.SCHEDULE}/${b.schedule_id}?occurrence_timestamp=${encodeURIComponent(
                      b.occurrence_timestamp
                    )}`
                  );
                }}
              >
                <Flex justify="space-between" align="center" gap={12}>
                  <Flex vertical style={{ minWidth: 0 }}>
                    <Text strong ellipsis>
                      {b.class_name}
                    </Text>
                    <Text style={{ fontSize: textPx(12.5), color: colors.ink2 }}>
                      {localTzDate(b.occurrence_timestamp).format(
                        `ddd, ${DEFAULT_DATE_FORMAT} · HH:mm`
                      )}
                    </Text>
                  </Flex>
                  <StatusBadge
                    tone={bookingStatusTone(b.status)}
                    label={getBookingStatusLabel(b.status)}
                  />
                </Flex>
              </button>
            ))}
          </Flex>
          <ConfigProvider theme={modalButtonsTheme}>
            <Flex justify="flex-end" style={{ marginTop: 22 }}>
              <Button onClick={onClose}>{translate("button.close")}</Button>
            </Flex>
          </ConfigProvider>
        </>
      );
    }
    return (
      <>
        <ModalHeader tone="error" title={translate("client_status.deactivate.title")} />
        <Text style={modalSubtitleStyle}>
          {translate("client_status.deactivate.body")}
        </Text>
        {livePackages.length > 0 && (
          <>
            <Text style={modalBlockLabelStyle}>
              {translate("client_status.deactivate.packages_note")}
            </Text>
            {/* One bordered row per package, like the block pop-up's
                bookings: name, then its balance and end date, then (shared
                packages only) that the other members keep using it. */}
            <Flex vertical gap={8}>
              {livePackages.map((p) => (
                <Flex key={p.id} vertical gap={2} style={{ ...rowStyle, cursor: "default" }}>
                  <Text strong>{p.name}</Text>
                  <Text style={{ fontSize: textPx(12.5), color: colors.ink2 }}>
                    {p.unlimited
                      ? translate("customer_package.unlimited")
                      : translateWithVars("client_status.credits_left", {
                          n: p.remaining_credits,
                        })}
                    {p.expiry_date &&
                      ` · ${translateWithVars("client_status.ends_on", {
                        // NBSP-joined so the date never wraps away from its label.
                        date: localTzDate(p.expiry_date).format(DEFAULT_DATE_FORMAT),
                      }).replace(/ /g, "\u00a0")}`}
                  </Text>
                  {/* Not named: with several co-sharers the names ran long. */}
                  {p.active_member_names.length > 0 && (
                    <Text style={{ fontSize: textPx(12.5), color: colors.ink2 }}>
                      {translate("client_status.deactivate.shared_note")}
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
          </>
        )}
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
            <Button onClick={onClose} disabled={deactivating}>
              {translate("button.cancel")}
            </Button>
            <Button type="primary" danger loading={deactivating} onClick={onConfirm}>
              {translate("button.deactivate_user")}
            </Button>
          </Flex>
        </ConfigProvider>
      </>
    );
  };

  return (
    <Modal
      open={open}
      footer={null}
      closable
      maskClosable={!deactivating}
      width={440}
      onCancel={onClose}
      destroyOnClose
    >
      {body()}
    </Modal>
  );
};
